import { useCallback, useMemo } from "react";

import { fromUnixTime, parseISO } from "date-fns";

import { useQuery } from "hooks";
import { createResolverFactory } from "components/QueryClient/ResolverFactory";

export default function useSubscriptionInfo(inviteCount) {
  const queryFactory = useCallback(() => {
    const query = `query subscriptionInfo($inviteCount: Int!) {
                    subscriptionInfo(inviteCount: $inviteCount) {
                      currency
                      billingPeriod
                      inTrial
                      trialEnd
                      coupons {
                        id
                        discountAmount
                        discountDetails
                      }
                      scheduledChangesAt
                      before {
                        name
                        planMultiple
                        usedSeats
                        numSeats
                        unitPrice
                        nextBillingPrice
                        nextBillingDate
                        deltaUsers
                        deltaSeats
                        deltaPrice
                      }
                      renewal {
                        name
                        planMultiple
                        unitPrice
                        usedSeats
                        numSeats
                        nextBillingPrice
                        deltaSeats
                        deltaPrice
                      }
                      after {
                        name
                        planMultiple
                        unitPrice
                        usedSeats
                        numSeats
                        nextBillingPrice
                        nextBillingDate
                      }
                    }
                  }`;

    const resolver = ({ data: { subscriptionInfo } }) => {
      const parseToDate = (dateString, parseFunction = fromUnixTime) => {
        return dateString ? parseFunction(dateString) : null;
      };

      const currency = subscriptionInfo?.currency;
      const billingPeriod = subscriptionInfo?.billingPeriod;
      const prepareEntry = (subscriptionEntry) => {
        if (subscriptionEntry) {
          return {
            ...subscriptionEntry,
            id: subscriptionEntry.name,
            currency: currency,
            billingPeriod: billingPeriod,
            nextBillingDate: parseToDate(subscriptionEntry.nextBillingDate),
          };
        }
        return null;
      };

      return {
        subscriptionInfo: {
          ...(subscriptionInfo?.coupons?.[0]
            ? { coupon: { ...subscriptionInfo?.coupons?.[0] } }
            : {}),
          inTrial: subscriptionInfo?.inTrial,
          trialEnd: parseToDate(subscriptionInfo?.trialEnd, parseISO),
          scheduledChangesAt: parseToDate(
            subscriptionInfo?.scheduledChangesAt,
            parseISO
          ),
          before: prepareEntry(subscriptionInfo?.before),
          renewal: prepareEntry(subscriptionInfo?.renewal),
          after: prepareEntry(subscriptionInfo?.after),
        },
      };
    };

    return { query: query, resolver: createResolverFactory(resolver) };
  }, []);

  const { loading, data, numOfFetches, error } = useQuery(
    queryFactory,
    useMemo(() => {
      return { variables: { inviteCount } };
    }, [inviteCount])
  );

  return {
    loading: !error && (loading || numOfFetches === 0),
    error: error
      ? error[0]?.message || "Failed to load subscription information"
      : "",
    ...data,
  };
}
